import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Icon, Typography } from "@/components/atoms";
import { useOperator } from "@/hooks";
import { useGetAppLinksQuery } from "@/redux/apis/config/linksAndDocuments";
import { useGetOnboardingStatusQuery, useUpdateSectionStatusMutation } from "@/redux/apis/onboarding/onboardingApi";
import { useAppSelector } from "@/redux/hooks";
import { registrationSelector } from "@/redux/slices/marketing/selectors";
import { FleetSetupSection } from "@/redux/slices/onboarding/types";
import { copyToClipboard } from "@/utils";
import { SetupPanel, SetupPanelItem, WhyDoINeedModal, WalkthroughModal, ReadSetupGuideModal } from "../common";
import { quickSetupContent, quickSetupExplanations } from "../data/quickSetupContent";
import { isSectionComplete, isPostSimulationSectionsUnlocked } from "../helpers/onboardingHelpers";
import { useQuickSetupNavigation } from "../hooks";

const FleetSetupPaths = {
  MY_VEHICLES: "/vehicles/my-vehicles",
  MY_DRIVERS: "/drivers/my-drivers",
} as const;

enum ModalType {
  VEHICLES_WHY = "vehicles-why-do-i-need",
  VEHICLES_WALKTHROUGH = "vehicles-walkthrough",
  VEHICLES_DOCUMENTATION = "vehicles-documentation",
  DRIVERS_WHY = "drivers-why-do-i-need",
  DRIVERS_WALKTHROUGH = "drivers-walkthrough",
  DRIVERS_DOCUMENTATION = "drivers-documentation",
  PARTNERED_DRIVERS_WHY = "partnered-drivers-why-do-i-need",
  PARTNERED_DRIVERS_WALKTHROUGH = "partnered-drivers-walkthrough",
  PARTNERED_DRIVERS_DOCUMENTATION = "partnered-drivers-documentation",
}

export const FleetSetupPanel = () => {
  const navigate = useNavigate();
  const { setQuickSetupFlag } = useQuickSetupNavigation();
  const [activeModal, setActiveModal] = useState<ModalType | null>(null);
  const { providerCode } = useOperator();

  const { isFetching } = useGetAppLinksQuery();
  const { appLinks } = useAppSelector(registrationSelector);
  const appDownload = appLinks.find((link) => link.document.slug === "app-driver");

  const { data: onboardingStatus } = useGetOnboardingStatusQuery();
  const [updateSectionStatus] = useUpdateSectionStatusMutation();

  const isVehiclesCompleted = onboardingStatus ? isSectionComplete(onboardingStatus.fleetSetup.addOwnVehicles) : false;
  const isDriversCompleted = onboardingStatus ? isSectionComplete(onboardingStatus.fleetSetup.addOwnDrivers) : false;
  const isPartneredDriversCompleted = onboardingStatus ? isSectionComplete(onboardingStatus.fleetSetup.inviteOwnPartnerDrivers) : false;

  const completedSteps = [isVehiclesCompleted, isDriversCompleted, isPartneredDriversCompleted].filter(Boolean).length;
  const totalSteps = 3;

  const isUnlocked = isPostSimulationSectionsUnlocked(onboardingStatus);

  const handleModalClose = () => setActiveModal(null);

  const handleCopyDownloadLink = async () => {
    if (appDownload?.document.link) {
      copyToClipboard(appDownload.document.link);

      if (onboardingStatus) {
        try {
          if (!isDriversCompleted) {
            await updateSectionStatus({ section: FleetSetupSection.ADD_OWN_DRIVERS, completed: true });
          } else if (!isPartneredDriversCompleted) {
            await updateSectionStatus({ section: FleetSetupSection.INVITE_OWN_PARTNER_DRIVERS, completed: true });
          }
        } catch (error) {
          console.error("Failed to update section status:", error);
        }
      }
    }
  };

  const handleVehiclesAction = () => {
    setQuickSetupFlag(FleetSetupSection.ADD_OWN_VEHICLES);
    navigate(FleetSetupPaths.MY_VEHICLES);

    if (onboardingStatus && !isVehiclesCompleted) {
      updateSectionStatus({ section: FleetSetupSection.ADD_OWN_VEHICLES, completed: true }).catch((error) => {
        console.error("Failed to update section status:", error);
      });
    }
  };

  const handleVehiclesWhyDoThis = () => {
    setActiveModal(ModalType.VEHICLES_WHY);
  };

  const handleVehiclesWatchVideo = () => {
    setActiveModal(ModalType.VEHICLES_WALKTHROUGH);
  };

  const handleVehiclesReadDocumentation = () => {
    setActiveModal(ModalType.VEHICLES_DOCUMENTATION);
  };

  const handleDriversAction = () => {
    setQuickSetupFlag(FleetSetupSection.ADD_OWN_DRIVERS);
    navigate(FleetSetupPaths.MY_DRIVERS);

    if (onboardingStatus && !isDriversCompleted) {
      updateSectionStatus({ section: FleetSetupSection.ADD_OWN_DRIVERS, completed: true }).catch((error) => {
        console.error("Failed to update section status:", error);
      });
    }
  };

  const handleDriversWhyDoThis = () => {
    setActiveModal(ModalType.DRIVERS_WHY);
  };

  const handleDriversWatchVideo = () => {
    setActiveModal(ModalType.DRIVERS_WALKTHROUGH);
  };

  const handleDriversReadDocumentation = () => {
    setActiveModal(ModalType.DRIVERS_DOCUMENTATION);
  };

  const handlePartneredDriversAction = () => {
    setQuickSetupFlag(FleetSetupSection.INVITE_OWN_PARTNER_DRIVERS);
    navigate(FleetSetupPaths.MY_DRIVERS);

    if (onboardingStatus && !isPartneredDriversCompleted) {
      updateSectionStatus({ section: FleetSetupSection.INVITE_OWN_PARTNER_DRIVERS, completed: true }).catch((error) => {
        console.error("Failed to update section status:", error);
      });
    }
  };

  const handlePartneredDriversWhyDoThis = () => {
    setActiveModal(ModalType.PARTNERED_DRIVERS_WHY);
  };

  const handlePartneredDriversWatchVideo = () => {
    setActiveModal(ModalType.PARTNERED_DRIVERS_WALKTHROUGH);
  };

  const handlePartneredDriversReadDocumentation = () => {
    setActiveModal(ModalType.PARTNERED_DRIVERS_DOCUMENTATION);
  };

  const vehiclesFeaturedIcon = {
    name: "Car",
    background: "bg-primary-light",
  };

  const driversFeaturedIcon = {
    name: "UserSquare",
    background: "bg-primary-light",
  };

  const partneredDriversFeaturedIcon = {
    name: "UserSquare",
    background: "bg-primary-light",
  };

  const transportProviderCodeContent = (
    <div className="rounded-lg bg-neutral-surface-gray p-2.5">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-2.5 md:flex-row md:items-center">
          <div className="flex items-center gap-1">
            <Icon isCustom name="app-google-playstore" size="sm" />
            <Icon isCustom name="app-ios" size="sm" />
          </div>
          <Typography variant="action" className="text-neutral-dark-gray">
            Transport Provider Code:
          </Typography>
          <div className="flex items-center gap-1">
            <Typography variant="paragraph" className="uppercase text-neutral-dark-gray">
              {providerCode}
            </Typography>
            <Icon name="InfoCircle" size="md" className="text-neutral-dark-gray" />
          </div>
        </div>
        <Button
          variant="tertiary-light"
          size="xs"
          className="self-start !p-0 hover:bg-transparent"
          startIcon={<Icon name="Copy" size="md" className="text-info" />}
          onClick={handleCopyDownloadLink}
          disabled={isFetching || !appDownload?.document.link}
        >
          <Typography variant="action" className="text-info">
            Copy Download Link
          </Typography>
        </Button>
      </div>
    </div>
  );

  return (
    <SetupPanel
      stepNumber={4}
      title="Fleet Setup"
      description="Add your vehicles and drivers to get your fleet ready for bookings and assignments"
      stepCount={totalSteps}
      completedSteps={completedSteps}
      isLocked={!isUnlocked}
    >
      <SetupPanelItem
        icon={vehiclesFeaturedIcon.name}
        iconBackground={vehiclesFeaturedIcon.background}
        title="Add Your Own Vehicles to Your Fleet"
        isCompleted={isVehiclesCompleted}
        actions={[
          {
            label: "Update Details",
            onClick: handleVehiclesAction,
            variant: "primary",
          },
        ]}
        helpOptions={[
          {
            label: "Why do I need to do this?",
            icon: <Icon name="help-circle" size="md" isCustom variant="Outline" />,
            onClick: handleVehiclesWhyDoThis,
          },
          { label: "Watch walkthrough video", icon: "Video", onClick: handleVehiclesWatchVideo },
          { label: "Read Setup Guide", icon: "Document", onClick: handleVehiclesReadDocumentation },
        ]}
      />

      <SetupPanelItem
        icon={driversFeaturedIcon.name}
        iconBackground={driversFeaturedIcon.background}
        title="Add Your Own Drivers"
        isCompleted={isDriversCompleted}
        actions={[
          {
            label: "Update Details",
            onClick: handleDriversAction,
            variant: "primary",
          },
        ]}
        additionalContent={transportProviderCodeContent}
        helpOptions={[
          {
            label: "Why do I need to do this?",
            icon: <Icon name="help-circle" size="md" isCustom variant="Outline" />,
            onClick: handleDriversWhyDoThis,
          },
          { label: "Watch walkthrough video", icon: "Video", onClick: handleDriversWatchVideo },
          { label: "Read Setup Guide", icon: "Document", onClick: handleDriversReadDocumentation },
        ]}
      />

      <SetupPanelItem
        icon={partneredDriversFeaturedIcon.name}
        iconBackground={partneredDriversFeaturedIcon.background}
        title="Invite Your Own Partnered Drivers"
        isCompleted={isPartneredDriversCompleted}
        actions={[
          {
            label: "Update Details",
            onClick: handlePartneredDriversAction,
            variant: "primary",
          },
        ]}
        additionalContent={transportProviderCodeContent}
        helpOptions={[
          {
            label: "Why do I need to do this?",
            icon: <Icon name="help-circle" size="md" isCustom variant="Outline" />,
            onClick: handlePartneredDriversWhyDoThis,
          },
          { label: "Watch walkthrough video", icon: "Video", onClick: handlePartneredDriversWatchVideo },
          { label: "Read Setup Guide", icon: "Document", onClick: handlePartneredDriversReadDocumentation },
        ]}
      />

      {/* Vehicles Modals */}
      <WhyDoINeedModal
        open={activeModal === ModalType.VEHICLES_WHY}
        onClose={handleModalClose}
        title="Why do I need to add vehicles to my fleet?"
        featuredIcon={vehiclesFeaturedIcon}
        explanations={quickSetupExplanations.fleetSetup.vehicles}
      />

      <WalkthroughModal
        featuredIcon={vehiclesFeaturedIcon}
        open={activeModal === ModalType.VEHICLES_WALKTHROUGH}
        onClose={handleModalClose}
        title="Video Walkthrough (Vehicle Management)"
        videoUrl={quickSetupContent.fleetSetup.vehicles.videoUrl}
      />

      <ReadSetupGuideModal
        featuredIcon={vehiclesFeaturedIcon}
        open={activeModal === ModalType.VEHICLES_DOCUMENTATION}
        onClose={handleModalClose}
        title="Vehicle Management Setup Guide"
        documentationUrl={quickSetupContent.fleetSetup.vehicles.documentationUrl}
        pdfUrl={quickSetupContent.fleetSetup.vehicles.pdfUrl}
      />

      {/* Drivers Modals */}
      <WhyDoINeedModal
        open={activeModal === ModalType.DRIVERS_WHY}
        onClose={handleModalClose}
        title="Why do I need to add drivers to my system?"
        featuredIcon={driversFeaturedIcon}
        explanations={quickSetupExplanations.fleetSetup.drivers}
      />

      <WalkthroughModal
        featuredIcon={driversFeaturedIcon}
        open={activeModal === ModalType.DRIVERS_WALKTHROUGH}
        onClose={handleModalClose}
        title="Video Walkthrough (Driver Management)"
        videoUrl={quickSetupContent.fleetSetup.drivers.videoUrl}
      />

      <ReadSetupGuideModal
        featuredIcon={driversFeaturedIcon}
        open={activeModal === ModalType.DRIVERS_DOCUMENTATION}
        onClose={handleModalClose}
        title="Driver Management Setup Guide"
        documentationUrl={quickSetupContent.fleetSetup.drivers.documentationUrl}
        pdfUrl={quickSetupContent.fleetSetup.drivers.pdfUrl}
      />

      {/* Partnered Drivers Modals */}
      <WhyDoINeedModal
        open={activeModal === ModalType.PARTNERED_DRIVERS_WHY}
        onClose={handleModalClose}
        title="Why do I need to invite partnered drivers?"
        featuredIcon={partneredDriversFeaturedIcon}
        explanations={quickSetupExplanations.fleetSetup.partneredDrivers}
      />

      <WalkthroughModal
        featuredIcon={partneredDriversFeaturedIcon}
        open={activeModal === ModalType.PARTNERED_DRIVERS_WALKTHROUGH}
        onClose={handleModalClose}
        title="Video Walkthrough (Partnered Driver Management)"
        videoUrl={quickSetupContent.fleetSetup.partneredDrivers.videoUrl}
      />

      <ReadSetupGuideModal
        featuredIcon={partneredDriversFeaturedIcon}
        open={activeModal === ModalType.PARTNERED_DRIVERS_DOCUMENTATION}
        onClose={handleModalClose}
        title="Partnered Driver Management Setup Guide"
        documentationUrl={quickSetupContent.fleetSetup.partneredDrivers.documentationUrl}
        pdfUrl={quickSetupContent.fleetSetup.partneredDrivers.pdfUrl}
      />
    </SetupPanel>
  );
};
